/**
 * m3 echo Frontend - Autopilot Management Card
 *
 * Component for managing autopilots on the project detail page.
 */

import React, { useState } from 'react';
import {
  Card,
  CardHeader,
  CardContent,
  Box,
  Button,
  Divider,
  Stack,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Switch,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  listAutopilots,
  toggleAutopilot,
  deleteAutopilot,
  getAutopilotQuota,
  Autopilot,
  QuotaBooking,
  ScheduleType,
} from '../services/autopilots';
import CreateAutopilotDialog from './CreateAutopilotDialog';
import MonthlyQuotaPanel from './quota/MonthlyQuotaPanel';
import { invalidateQuotaViews } from './quota/quotaQueries';
import { describeApiError } from '../services/api';
import { useCanEdit } from '../stores/useAuthStore';

/**
 * Ledger wording: the cadence only, no weekday and no time. A booking row says
 * what was spent and why; when it runs belongs to the schedule line above,
 * which names the day, and to the create dialog, which needs it to choose.
 */
const CADENCE_LABELS: Record<ScheduleType, string> = {
  daily: 'Täglich',
  weekly: 'Wöchentlich',
  monthly: 'Monatlich',
};

/**
 * Why an amount was booked. A month can hold several bookings for the same
 * autopilot - the opening one, an increase after the prompts changed, the
 * renewal on the 1st - so the rows carry their reason, otherwise the same
 * project appearing twice reads like a bug.
 */
const BOOKING_KIND_LABELS: Record<QuotaBooking['kind'], string> = {
  initial: 'Erstbuchung',
  renewal: 'Monatsverlängerung',
  increase: 'Erhöhung',
  admin_adjustment: 'Manuelle Anpassung',
};

interface Props {
  projectId: number;
  projectName: string;
  tenantId?: number; // v4.8.3 - needed for admin users viewing tenant projects
  createDialogOpen?: boolean;
  onOpenCreateDialog?: () => void;
  onCloseCreateDialog?: () => void;
}

export const AutopilotManagementCard: React.FC<Props> = ({
  projectId,
  projectName,
  tenantId,
  createDialogOpen: externalCreateDialogOpen,
  onOpenCreateDialog,
  onCloseCreateDialog,
}) => {
  const queryClient = useQueryClient();
  const canEdit = useCanEdit();
  const [internalCreateDialogOpen, setInternalCreateDialogOpen] = useState(false);

  // Use external state if provided, otherwise use internal state
  const createDialogOpen = externalCreateDialogOpen !== undefined ? externalCreateDialogOpen : internalCreateDialogOpen;
  const _handleOpenDialog = onOpenCreateDialog || (() => setInternalCreateDialogOpen(true));
  const handleCloseDialog = onCloseCreateDialog || (() => setInternalCreateDialogOpen(false));
  void _handleOpenDialog; // Silence unused variable warning - kept for potential future use

  const { data: autopilots, isLoading } = useQuery({
    queryKey: ['autopilots', projectId, tenantId],
    queryFn: () => listAutopilots({ project_id: projectId, tenant_id: tenantId }),
  });

  const {
    data: quota,
    isError: quotaFailed,
    isFetching: quotaFetching,
    refetch: refetchQuota,
  } = useQuery({
    queryKey: ['autopilotQuota', tenantId],
    queryFn: () => getAutopilotQuota(tenantId),
  });

  /**
   * Why a refused pause/resume/delete has somewhere to be said.
   *
   * Resuming is a booking, not a flag flip: `toggle_autopilot_status` calls
   * `book_increase`, so an autopilot paused before the quota was lowered can
   * cost more at its full-month price than the limit now carries, and the
   * server refuses it. Without this the refusal arrived as nothing at all -
   * the switch sprang back and the customer was left guessing.
   */
  const [actionError, setActionError] = useState<string | null>(null);

  /**
   * Deliberately no preview before resuming, unlike creating.
   *
   * A preview earns its round trip when it informs a choice: the create dialog
   * offers three cadences and a day, and the two numbers are what the customer
   * picks between. Resume offers nothing to pick - the configuration is fixed,
   * and a preview could only say a moment earlier what the refusal says, at
   * the cost of a request per row and a confirmation step on the common case,
   * which costs nothing at all because the month is already paid for. What was
   * genuinely missing is the refusal itself, and the server's own sentence
   * names which check failed and by how much.
   */
  const toggleMutation = useMutation({
    mutationFn: ({ id, is_active }: { id: number; is_active: boolean }) =>
      toggleAutopilot(id, is_active),
    onMutate: () => setActionError(null),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['autopilots', projectId] });
      invalidateQuotaViews(queryClient);
    },
    onError: (err: unknown, variables) => {
      setActionError(
        describeApiError(
          err,
          variables.is_active
            ? 'Der Autopilot konnte nicht fortgesetzt werden.'
            : 'Der Autopilot konnte nicht pausiert werden.'
        )
      );
    },
  });

  const deleteMutation = useMutation({
    mutationFn: deleteAutopilot,
    onMutate: () => setActionError(null),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['autopilots', projectId] });
      invalidateQuotaViews(queryClient);
    },
    onError: (err: unknown) => {
      setActionError(describeApiError(err, 'Der Autopilot konnte nicht gelöscht werden.'));
    },
  });

  const handleToggle = (autopilot: Autopilot) => {
    toggleMutation.mutate({ id: autopilot.id, is_active: !autopilot.is_active });
  };

  const handleDelete = (autopilotId: number) => {
    if (window.confirm('Möchten Sie diesen Autopilot wirklich löschen?')) {
      deleteMutation.mutate(autopilotId);
    }
  };

  /**
   * The API's timestamps are naive UTC - the columns are bare `TIMESTAMP` and
   * `AutopilotService.calculate_next_run_time` documents its return value as
   * naive UTC - so they arrive without a zone marker. `new Date()` reads such a
   * string as *local* time, which printed "Nächster Lauf: 05:00" directly under
   * a schedule line reading "Täglich um 07:00 Uhr": off by the viewer's UTC
   * offset, two hours in summer and one in winter.
   *
   * A value that already carries a zone is passed through untouched, so this
   * keeps working if the column ever becomes timezone-aware. The `T` guard
   * keeps a bare date from being turned into `2026-09-02Z`, which is not a
   * valid instant.
   */
  const parseNaiveUtc = (value: string): Date => {
    const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value);
    return new Date(value.includes('T') && !hasZone ? `${value}Z` : value);
  };

  const getScheduleText = (autopilot: Autopilot) => {
    if (autopilot.autopilot_type === 'benchmark') {
      const days = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
      return `Wöchentlich ${days[autopilot.schedule_day_of_week || 0]}`;
    }
    // Read from the row rather than assumed: the cadence is chosen when the
    // autopilot is created, and it is what the monthly booking is priced on.
    // An unrecognised cadence says so - falling through to "Täglich" would put
    // the same confident wrong answer back that this replaced.
    if (autopilot.schedule_type === 'daily') {
      return 'Täglich um 07:00 Uhr';
    }
    if (autopilot.schedule_type === 'weekly') {
      return 'Wöchentlich montags um 07:00 Uhr';
    }
    if (autopilot.schedule_type === 'monthly') {
      return autopilot.schedule_day_of_month
        ? `Monatlich am ${autopilot.schedule_day_of_month}. um 07:00 Uhr`
        : 'Monatlich um 07:00 Uhr';
    }
    return 'N/A';
  };

  const getNextRunText = (autopilot: Autopilot) => {
    if (!autopilot.next_run_at) {
      if (autopilot.runs_after_llm) {
        return 'Nach LLM-Test';
      }
      return 'Nicht geplant';
    }
    return parseNaiveUtc(autopilot.next_run_at).toLocaleString('de-DE', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" justifyContent="center" p={3}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader title="Autopilots" />
        <CardContent>
          {/*
            Above the list, and a live region, because the control that failed
            is a switch that has already sprung back to where it was: there is
            nothing left on screen pointing at the action, so the sentence has
            to announce itself.
          */}
          <Box role="status" aria-live="polite">
            {actionError && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
                {actionError}
              </Alert>
            )}
          </Box>
          {/*
            The quota panel gets the card's full width rather than the header's
            subheader slot: both of its bars share one scale, so the bar that
            fits renders shorter than the box whenever its sibling overruns, and
            a narrow column would squeeze the part that carries the meaning.
          */}
          {quota && (
            <Box sx={{ mb: 3 }}>
              <MonthlyQuotaPanel quota={quota} />
              {quota.bookings.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Buchungen in diesem Monat
                  </Typography>
                  <Divider />
                  <Stack divider={<Divider />}>
                    {quota.bookings.map((booking, index) => (
                      <Box
                        key={`${booking.autopilot_id ?? 'none'}-${booking.kind}-${index}`}
                        sx={{
                          display: 'flex',
                          alignItems: 'baseline',
                          justifyContent: 'space-between',
                          gap: 2,
                          py: 0.75,
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body2" noWrap>
                            {/*
                              A booking outlives its autopilot, so a paused or
                              deleted one leaves the project unnamed rather than
                              dropping the amount that is still committed.
                            */}
                            {booking.project_name ?? 'Ohne Projekt'}
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            {[
                              booking.schedule_type ? CADENCE_LABELS[booking.schedule_type] : null,
                              BOOKING_KIND_LABELS[booking.kind],
                            ]
                              .filter(Boolean)
                              .join(' · ')}
                          </Typography>
                        </Box>
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                        >
                          {booking.amount > 0 ? `+${booking.amount}` : booking.amount} Prompts
                        </Typography>
                      </Box>
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>
          )}
          {/*
            A failed quota read used to take the whole panel with it - no bars,
            no ledger, no message, nothing to retry - which is the same defect
            the admin card had and the create dialog now explains. The customer
            side says it too.
          */}
          {!quota && quotaFailed && (
            <Alert
              severity="error"
              sx={{ mb: 3 }}
              action={
                <Button
                  color="inherit"
                  size="small"
                  onClick={() => refetchQuota()}
                  disabled={quotaFetching}
                >
                  Erneut laden
                </Button>
              }
            >
              Das Monatskontingent konnte nicht geladen werden.
            </Alert>
          )}
          {!autopilots || autopilots.filter(a => a.autopilot_type !== 'benchmark').length === 0 ? (
            <Alert severity="info">
              Noch keine Autopilots für dieses Projekt erstellt.
              Erstellen Sie einen Autopilot, um automatische Tests zu planen.
            </Alert>
          ) : (
            <List>
              {autopilots.filter(a => a.autopilot_type !== 'benchmark').map((autopilot) => (
                <ListItem key={autopilot.id} divider>
                  <Box sx={{ mr: 2 }}>
                    {autopilot.autopilot_type === 'llm_score' ? (
                      <TrendingUpIcon color="primary" />
                    ) : (
                      <ScheduleIcon color="secondary" />
                    )}
                  </Box>
                  <ListItemText
                    // Both slots hold a Box, and both default to a Typography
                    // that renders <p> - a div inside a p, which the browser
                    // silently reparents. Long-standing; it only became visible
                    // once this list was rendered under a DOM validator.
                    primaryTypographyProps={{ component: 'div' }}
                    secondaryTypographyProps={{ component: 'div' }}
                    primary={
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body1">
                          {autopilot.autopilot_type === 'llm_score' ? 'LLM Score Test' : 'Optimierung'}
                        </Typography>
                        <Chip
                          label={autopilot.is_active ? 'Aktiv' : 'Pausiert'}
                          color={autopilot.is_active ? 'success' : 'default'}
                          size="small"
                        />
                        {autopilot.prompt_count > 0 && (
                          <Chip
                            label={`${autopilot.prompt_count} Prompt${autopilot.prompt_count > 1 ? 's' : ''}`}
                            size="small"
                            variant="outlined"
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography variant="body2" color="textSecondary">
                          Zeitplan: {getScheduleText(autopilot)}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Nächster Lauf: {getNextRunText(autopilot)}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          Läufe gesamt: {autopilot.total_runs}
                        </Typography>
                      </Box>
                    }
                  />
                  {canEdit && (
                    <ListItemSecondaryAction>
                      <Tooltip title={autopilot.is_active ? 'Pausieren' : 'Fortsetzen'}>
                        <Switch
                          checked={autopilot.is_active}
                          onChange={() => handleToggle(autopilot)}
                          disabled={toggleMutation.isPending}
                        />
                      </Tooltip>
                      <Tooltip title="Löschen">
                        <IconButton
                          edge="end"
                          onClick={() => handleDelete(autopilot.id)}
                          disabled={deleteMutation.isPending}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </ListItemSecondaryAction>
                  )}
                </ListItem>
              ))}
            </List>
          )}
        </CardContent>
      </Card>

      <CreateAutopilotDialog
        open={createDialogOpen}
        onClose={handleCloseDialog}
        projectId={projectId}
        projectName={projectName}
        quota={quota}
        quotaError={quotaFailed}
        tenantId={tenantId}
      />
    </>
  );
};

export default AutopilotManagementCard;
